import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, LogOut, LayoutDashboard, Users, Mail, Wrench as WrenchIcon,
  Image as ImageIcon, Megaphone, Settings as SettingsIcon, Package,
  PiggyBank, Receipt, FileCheck2, Briefcase,
} from 'lucide-react';
import AdminGallery from '../components/admin/AdminGallery';
import AdminPricing from '../components/admin/AdminPricing';
import AdminBanner from '../components/admin/AdminBanner';
import AdminInbox from '../components/admin/AdminInbox';
import AdminCustomers from '../components/admin/AdminCustomers';
import AdminLedger from '../components/admin/AdminLedger';
import AdminEmails from '../components/admin/AdminEmails';
import AdminEstimates from '../components/admin/AdminEstimates';
import AdminInvoices from '../components/admin/AdminInvoices';
import AdminWebsite from '../components/admin/AdminWebsite';
import AdminCatalog from '../components/admin/AdminCatalog';
import AdminSettings from '../components/admin/AdminSettings';
import { API, TOKEN_KEY, apiErr, authHeaders } from '../components/admin/adminApi';

const LoginPanel = ({ onAuthed }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuthed();
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800" data-testid="admin-login-card">
        <CardHeader>
          <CardTitle className="text-white text-2xl">TayLord Arms — Admin</CardTitle>
          <p className="text-gray-400 text-sm">Sign in to manage the site</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email" data-testid="admin-login-email" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                required className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <Input
                id="password" data-testid="admin-login-password" type="password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                required className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            {error && <p data-testid="admin-login-error" className="text-red-400 text-sm">{error}</p>}
            <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
            <Link to="/" className="block text-center text-sm text-gray-500 hover:text-gray-300">
              ← Back to site
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Core (always-visible) tab definitions. Order = display order in the nav.
const CORE_TABS = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'customers', label: 'Customers', icon: Users },
  { value: 'emails', label: 'Emails', icon: Mail },
  { value: 'services', label: 'Services', icon: Briefcase },
  { value: 'catalog', label: 'Catalog', icon: Package },
  { value: 'gallery', label: 'Gallery', icon: ImageIcon },
  { value: 'website', label: 'Website', icon: WrenchIcon },
  { value: 'announcements', label: 'Announcements', icon: Megaphone },
  { value: 'settings', label: 'Settings', icon: SettingsIcon },
];

// Legacy tabs — only rendered when un-archived from Settings.
const LEGACY_TABS = [
  { value: 'ledger', label: 'Ledger', icon: PiggyBank, key: 'ledger' },
  { value: 'estimates', label: 'Estimates', icon: Receipt, key: 'estimates' },
  { value: 'invoices', label: 'Invoices', icon: FileCheck2, key: 'invoices' },
];

const DEFAULT_TAB = 'dashboard';

const Dashboard = ({ onLogout }) => {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('admin:lastTab') || DEFAULT_TAB; } catch { return DEFAULT_TAB; }
  });

  useEffect(() => {
    try { localStorage.setItem('admin:lastTab', tab); } catch { /* storage disabled */ }
  }, [tab]);

  // Fetch site settings once — drives which legacy tabs appear
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/site-settings`, { headers: authHeaders() });
        if (!cancelled) setSettings(data);
      } catch (err) {
        if (err?.response?.status === 401) { onLogout(); return; }
      }
    })();
    return () => { cancelled = true; };
  }, [onLogout]);

  // Allow child tabs to request a tab switch
  useEffect(() => {
    const handler = (ev) => {
      const next = ev.detail;
      if (typeof next === 'string') setTab(next);
    };
    window.addEventListener('admin:goto-tab', handler);
    return () => window.removeEventListener('admin:goto-tab', handler);
  }, []);

  const archived = settings?.archived_modules || { ledger: true, estimates: true, invoices: true };
  const visibleLegacyTabs = LEGACY_TABS.filter((t) => !archived[t.key]);

  // If the persisted tab is now hidden (archived legacy tab), fall back to dashboard.
  useEffect(() => {
    const allValues = [...CORE_TABS.map((t) => t.value), ...visibleLegacyTabs.map((t) => t.value)];
    if (!allValues.includes(tab)) setTab(DEFAULT_TAB);
  }, [tab, visibleLegacyTabs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-dashboard">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Site Admin</h1>
            <p className="text-xs text-gray-500">TayLord Arms</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="outline" size="sm" className="border-slate-700 text-white hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" /> View site
              </Button>
            </Link>
            <Button data-testid="admin-logout" variant="outline" size="sm" onClick={onLogout} className="border-slate-700 text-white hover:bg-slate-800">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800 mb-6 flex-wrap h-auto">
            {CORE_TABS.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                data-testid={`tab-${value}`}
                value={value}
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
              >
                <Icon className="w-4 h-4 mr-2" /> {label}
              </TabsTrigger>
            ))}
            {visibleLegacyTabs.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                data-testid={`tab-${value}`}
                value={value}
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
              >
                <Icon className="w-4 h-4 mr-2" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Core tabs — Dashboard reuses the existing AdminInbox (now includes DashboardOverview).
              Emails reuses AdminTemplates; Services reuses AdminPricing;
              Announcements reuses AdminBanner. Nothing lost, everything renamed. */}
          <TabsContent value="dashboard"><AdminInbox onUnauthorized={onLogout} /></TabsContent>
          <TabsContent value="customers"><AdminCustomers onUnauthorized={onLogout} /></TabsContent>
          <TabsContent value="emails"><AdminEmails onUnauthorized={onLogout} /></TabsContent>
          <TabsContent value="services"><AdminPricing onUnauthorized={onLogout} /></TabsContent>
          <TabsContent value="catalog"><AdminCatalog /></TabsContent>
          <TabsContent value="gallery"><AdminGallery onUnauthorized={onLogout} /></TabsContent>
          <TabsContent value="website"><AdminWebsite /></TabsContent>
          <TabsContent value="announcements"><AdminBanner onUnauthorized={onLogout} /></TabsContent>
          <TabsContent value="settings">
            <AdminSettings onUnauthorized={onLogout} settings={settings} onSettingsChange={setSettings} />
          </TabsContent>

          {/* Legacy tabs — data preserved; only render when un-archived. */}
          {!archived.ledger && (
            <TabsContent value="ledger"><AdminLedger onUnauthorized={onLogout} /></TabsContent>
          )}
          {!archived.estimates && (
            <TabsContent value="estimates"><AdminEstimates onUnauthorized={onLogout} /></TabsContent>
          )}
          {!archived.invoices && (
            <TabsContent value="invoices"><AdminInvoices onUnauthorized={onLogout} /></TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

const Admin = () => {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  const verify = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setAuthed(false); setChecking(false); return; }
    try {
      await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (checking) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-gray-400">Loading…</div>;
  }
  return authed ? <Dashboard onLogout={logout} /> : <LoginPanel onAuthed={() => setAuthed(true)} />;
};

export default Admin;
